import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit2,
  Trash2,
  Eye,
  Copy,
  Building2,
} from 'lucide-react';
import {
  Card,
  Badge,
  Button,
  ShimmerButton,
  Input,
  ResponsiveDialog,
  Tabs,
  EmptyState,
  useToast,
  cn,
  formatNaira,
  formatNumber,
  formatDate,
  staggerChildren,
  fadeInUp,
} from '@raffle/ui';

// Mock data
const mockProperties = [
  {
    id: '1',
    title: 'Luxury Villa in Lekki Phase 1',
    location: 'Lekki Phase 1, Lagos',
    marketValue: 450000000,
    ticketPrice: 1000,
    totalTickets: 10000,
    ticketsSold: 7234,
    drawDate: new Date('2026-02-28'),
    status: 'active' as const,
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=400',
    createdAt: new Date('2024-01-01'),
  },
  {
    id: '2',
    title: 'Modern Penthouse in Victoria Island',
    location: 'Victoria Island, Lagos',
    marketValue: 380000000,
    ticketPrice: 1000,
    totalTickets: 12000,
    ticketsSold: 4521,
    drawDate: new Date('2026-03-15'),
    status: 'active' as const,
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400',
    createdAt: new Date('2024-01-05'),
  },
  {
    id: '3',
    title: 'Executive Mansion in Banana Island',
    location: 'Banana Island, Lagos',
    marketValue: 850000000,
    ticketPrice: 1000,
    totalTickets: 10000,
    ticketsSold: 2156,
    drawDate: new Date('2026-04-01'),
    status: 'active' as const,
    image: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?w=400',
    createdAt: new Date('2024-01-10'),
  },
  {
    id: '4',
    title: 'Beachfront Apartment in Oniru',
    location: 'Oniru, Victoria Island',
    marketValue: 180000000,
    ticketPrice: 1000,
    totalTickets: 8000,
    ticketsSold: 8000,
    drawDate: new Date('2024-01-15'),
    status: 'drawn' as const,
    image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=400',
    createdAt: new Date('2023-12-01'),
  },
];

type PropertyStatus = 'all' | 'active' | 'pending' | 'drawn' | 'cancelled';

export const PropertiesPage: React.FC = () => {
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PropertyStatus>('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null);

  const filteredProperties = useMemo(() => {
    return mockProperties.filter((property) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!property.title.toLowerCase().includes(query) && !property.location.toLowerCase().includes(query)) {
          return false;
        }
      }
      if (statusFilter !== 'all' && property.status !== statusFilter) {
        return false;
      }
      return true;
    });
  }, [searchQuery, statusFilter]);

  const stats = useMemo(() => ({
    total: mockProperties.length,
    active: mockProperties.filter((p) => p.status === 'active').length,
    pending: mockProperties.filter((p) => p.status === 'pending').length,
    drawn: mockProperties.filter((p) => p.status === 'drawn').length,
  }), []);

  const statusTabs = [
    { value: 'all' as const, label: 'All', count: stats.total },
    { value: 'active' as const, label: 'Active', count: stats.active },
    { value: 'pending' as const, label: 'Pending', count: stats.pending },
    { value: 'drawn' as const, label: 'Completed', count: stats.drawn },
  ];

  const handleDelete = (id: string) => {
    toast.success('Property Deleted', 'The property has been removed');
    setShowDeleteConfirm(null);
  };

  const handleDuplicate = (id: string) => {
    toast.success('Property Duplicated', 'A copy has been created');
    setShowActionsMenu(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-text-primary">Properties</h1>
          <p className="text-text-secondary">Manage your raffle properties</p>
        </div>
        <Link to="/properties/new">
          <ShimmerButton>
            <Plus className="w-4 h-4 mr-2" />
            Add Property
          </ShimmerButton>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <Input
            type="search"
            placeholder="Search properties..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Status Tabs */}
      <Tabs
        value={statusFilter}
        onChange={(value) => setStatusFilter(value as PropertyStatus)}
        tabs={statusTabs.map((tab) => ({
          value: tab.value,
          label: tab.label,
          badge: tab.count,
        }))}
      />

      {/* Properties Table */}
      {filteredProperties.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No properties found"
          description={
            searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Create your first property raffle'
          }
          action={
            searchQuery || statusFilter !== 'all'
              ? {
                  label: 'Clear Filters',
                  onClick: () => {
                    setSearchQuery('');
                    setStatusFilter('all');
                  },
                }
              : {
                  label: 'Add Property',
                  href: '/properties/new',
                }
          }
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full admin-data-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Value</th>
                  <th>Ticket Price</th>
                  <th>Progress</th>
                  <th>Draw Date</th>
                  <th>Status</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filteredProperties.map((property) => {
                  const progress = (property.ticketsSold / property.totalTickets) * 100;
                  
                  return (
                    <tr key={property.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <img
                            src={property.image}
                            alt=""
                            className="w-12 h-8 rounded-lg object-cover"
                          />
                          <div className="min-w-0">
                            <p className="font-medium text-text-primary truncate max-w-[200px]">
                              {property.title}
                            </p>
                            <p className="text-xs text-text-muted truncate">{property.location}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="font-mono tabular-nums">{formatNaira(property.marketValue)}</span>
                      </td>
                      <td>
                        <span className="font-mono tabular-nums">{formatNaira(property.ticketPrice)}</span>
                      </td>
                      <td>
                        <div className="w-32">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-text-muted">
                              {formatNumber(property.ticketsSold)} / {formatNumber(property.totalTickets)}
                            </span>
                            <span className="text-text-primary">{progress.toFixed(0)}%</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-surface-3 overflow-hidden">
                            <div
                              className="h-full bg-gold-500 rounded-full"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="text-text-secondary">{formatDate(property.drawDate)}</span>
                      </td>
                      <td>
                        <Badge
                          variant={
                            property.status === 'active'
                              ? 'success'
                              : property.status === 'pending'
                              ? 'warning'
                              : property.status === 'drawn'
                              ? 'info'
                              : 'default'
                          }
                        >
                          {property.status.charAt(0).toUpperCase() + property.status.slice(1)}
                        </Badge>
                      </td>
                      <td>
                        <div className="relative">
                          <button
                            onClick={() => setShowActionsMenu(showActionsMenu === property.id ? null : property.id)}
                            className="p-2 rounded-vault hover:bg-surface-2 transition-colors"
                          >
                            <MoreVertical className="w-4 h-4 text-text-muted" />
                          </button>

                          {showActionsMenu === property.id && (
                            <>
                              <div
                                className="fixed inset-0 z-40"
                                onClick={() => setShowActionsMenu(null)}
                              />
                              <div className="absolute right-0 top-full mt-1 w-40 py-1 bg-surface-2 border border-white/10 rounded-vault-lg shadow-xl z-50">
                                <Link
                                  to={`/properties/${property.id}/edit`}
                                  className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:bg-surface-3 hover:text-text-primary"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  Edit
                                </Link>
                                <button
                                  onClick={() => handleDuplicate(property.id)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:bg-surface-3 hover:text-text-primary"
                                >
                                  <Copy className="w-4 h-4" />
                                  Duplicate
                                </button>
                                <a
                                  href={`http://localhost:3000/properties/${property.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:bg-surface-3 hover:text-text-primary"
                                >
                                  <Eye className="w-4 h-4" />
                                  View Live
                                </a>
                                <button
                                  onClick={() => {
                                    setShowActionsMenu(null);
                                    setShowDeleteConfirm(property.id);
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-vault-error hover:bg-vault-error/10"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <ResponsiveDialog
        isOpen={!!showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(null)}
        title="Delete Property"
        modalSize="sm"
      >
        <div className="p-6 space-y-6">
          <p className="text-text-secondary">
            Are you sure you want to delete this property? This action cannot be undone and all associated tickets will be refunded.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setShowDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="danger" fullWidth onClick={() => showDeleteConfirm && handleDelete(showDeleteConfirm)}>
              Delete Property
            </Button>
          </div>
        </div>
      </ResponsiveDialog>
    </div>
  );
};
